import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock,
  ShieldCheck,
  UserCircle,
  AddressBookIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import CategoriaBadge from "../components/CategoriaBadge";
import { PERMISSIONS } from "../lib/permissions";
import { useAuth } from "@/context/AuthContext";

const PARENTESCOS = [
  { value: "partner", label: "Pareja" },
  { value: "parent", label: "Progenitor" },
  { value: "child", label: "Hijo/a" },
  { value: "sibling", label: "Hermano/a" },
  { value: "grandparent", label: "Abuelo/a" },
  { value: "grandchild", label: "Nieto/a" },
  { value: "guardian", label: "Tutor legal" },
  { value: "family", label: "Familiar" },
];

const PARENTESCOS_LABELS = {
  partner: "Pareja",
  parent: "Progenitor",
  child: "Hijo/a",
  sibling: "Hermano/a",
  grandparent: "Abuelo/a",
  grandchild: "Nieto/a",
  guardian: "Tutor legal",
  family: "Familiar",
};

const INVERSE_RELATION_LABELS = {
  parent: "Hijo/a",
  child: "Progenitor",
  partner: "Pareja",
  sibling: "Hermano/a",
  grandparent: "Nieto/a",
  grandchild: "Abuelo/a",
  guardian: "Tutelado/a",
  ward: "Tutor legal",
  family: "Familiar",
};

export default function MiPerfil() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editable, setEditable] = useState({});
  const [tab, setTab] = useState(searchParams.get("tab") || "perfil");
  const [passwords, setPasswords] = useState({
    actual: "",
    nueva: "",
    repetir: "",
  });
  const [family, setFamily] = useState([]);
  const [familySearch, setFamilySearch] = useState("");
  const [familyResults, setFamilyResults] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [parentesco, setParentesco] = useState("partner");
  const [loadingFamily, setLoadingFamily] = useState(false);
  const [familyRequests, setFamilyRequests] = useState([]);
  const { can, notificationsSummary, reloadNotifications, refreshUser } =
    useAuth();
  const canManage = can(PERMISSIONS.MANAGE_MEMBERS);

  const fetchProfile = async () => {
    try {
      const { data } = await api.get("/me/profile");
      setProfile(data);

      const fam = await api.get("/familias/mine");
      setFamily(fam.data);

      const requests = await api.get("/solicitudes/mine");
      setFamilyRequests(requests.data);
    } catch (err) {
      toast.error("No se pudo cargar el perfil");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();

    const run = async () => {
      if (familySearch.trim().length < 2) {
        setFamilyResults([]);
        return;
      }

      try {
        setLoadingFamily(true);
        const { data } = await api.get(`/falleros/search?q=${familySearch}`);
        setFamilyResults(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingFamily(false);
      }
    };

    const t = setTimeout(run, 300);

    return () => clearTimeout(t);
  }, [familySearch]);

  const saveMissingFields = async () => {
    try {
      await api.patch("/me/profile", editable);

      toast.success("Datos actualizados");

      fetchProfile();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudieron guardar los datos",
      );
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();

    if (passwords.nueva !== passwords.repetir) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    try {
      await api.post("/auth/change-password", {
        current_password: passwords.actual,

        new_password: passwords.nueva,
      });

      toast.success("Contraseña actualizada");

      setPasswords({
        actual: "",
        nueva: "",
        repetir: "",
      });
      await refreshUser();
    } catch (err) {
      const detail = err?.response?.data?.detail;

      toast.error(
        Array.isArray(detail)
          ? detail[0]?.msg
          : detail || "No se pudo cambiar la contraseña",
      );
    }
  };

  const removeFamily = async (id) => {
    try {
      await api.delete(`/familias/${id}`);
      toast.success("Vínculo eliminado");

      const fam = await api.get("/familias/mine");
      setFamily(fam.data);
      await reloadNotifications();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudo desvincular el familiar",
      );
    }
  };

  const requestRemoveFamily = async (id) => {
    try {
      await api.post(`/solicitudes/${id}/delete`);

      toast.success("Solicitud enviada");

      const requests = await api.get("/solicitudes/mine");
      setFamilyRequests(requests.data);
      await reloadNotifications();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudo enviar la solicitud",
      );
    }
  };

  if (loading) {
    return <div className="p-10 text-med-muted">Cargando perfil...</div>;
  }

  const fallero = profile?.fallero;
  const user = profile?.user;

  const fields = [
    {
      key: "nombre",
      label: "Nombre",
      value: fallero?.nombre,
    },
    {
      key: "apellidos",
      label: "Apellidos",
      value: fallero?.apellidos,
    },
    {
      key: "email",
      label: "Email",
      value: user?.email,
    },
    {
      key: "telefono",
      label: "Teléfono",
      value: fallero?.telefono || "—",
    },
    {
      key: "dni",
      label: "DNI",
      value: fallero?.dni || "—",
    },
    {
      key: "sexo",
      label: "Sexo",
      value: fallero?.sexo || "—",
    },
    {
      key: "direccion",
      label: "Dirección",
      value: fallero?.direccion || "—",

      full: true,
    },
    {
      key: "observaciones",
      label: "Observaciones",
      value: fallero?.observaciones || "—",

      full: true,
    },
  ];

  const receivedRequests = familyRequests.filter(
    (r) =>
      r.estado === "pending" &&
      r.approval_type === "user" &&
      r.objetivo_fallero_id === profile?.fallero?.id,
  );

  const sentRequests = familyRequests.filter(
    (r) =>
      r.estado === "pending" &&
      r.solicitante_fallero_id === profile?.fallero?.id,
  );

  return (
    <div className="p-6 md:p-10">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
          Cuenta
        </div>

        <h1 className="font-display text-4xl font-bold text-med-text">
          Mi perfil
        </h1>

        <p className="text-med-muted mt-2">
          Tus datos personales son gestionados por Secretaría. Los campos vacíos
          pueden completarse una única vez.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {[
          {
            key: "perfil",
            label: "Perfil",
            icon: UserCircle,
          },
          {
            key: "familia",
            label: "Familia",
            icon: AddressBookIcon,
          },
          {
            key: "seguridad",
            label: "Seguridad",
            icon: Lock,
          },
        ].map((t) => {
          const Icon = t.icon;

          return (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
              }}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                tab === t.key
                  ? "bg-med-primary text-white"
                  : "bg-med-surface border border-med-border text-med-text hover:border-med-primary"
              }`}
            >
              <Icon size={18} weight="duotone" />

              {t.label}
              {t.key === "familia" &&
                notificationsSummary?.user_pending_requests > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {notificationsSummary.user_pending_requests}
                  </span>
                )}
            </button>
          );
        })}
      </div>

      {/* PERFIL */}
      {tab === "perfil" && (
        <motion.div
          initial={{
            opacity: 0,
            y: 10,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="bg-med-surface border border-med-border rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl font-bold text-med-text">
              Datos personales
            </h2>

            {fallero?.categoria && (
              <CategoriaBadge
                categoria={fallero.categoria_aplicada || fallero.categoria}
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  {f.label}
                </label>

                {!f.value || f.value === "—" ? (
                  <input
                    value={editable[f.key] || ""}
                    onChange={(e) =>
                      setEditable((prev) => ({
                        ...prev,
                        [f.key]: e.target.value,
                      }))
                    }
                    className="
                                            w-full
                                            rounded-xl
                                            bg-white
                                            border-2
                                            border-med-primary/30
                                            px-4
                                            py-3
                                            outline-none
                                            transition-all
                                            placeholder:text-med-muted/60
                                            hover:border-med-primary/50
                                            focus:border-med-primary
                                            focus:ring-4
                                            focus:ring-med-primary/10
                                        "
                    placeholder={`Introduce ${f.label.toLowerCase()}`}
                  />
                ) : (
                  <div className="rounded-xl bg-med-surface2 border border-med-border px-4 py-3 text-med-text">
                    {f.value}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={saveMissingFields}
            className="mt-6 bg-med-primary hover:bg-med-primary-hover text-white rounded-xl px-5 py-3 font-semibold transition-colors"
          >
            Guardar datos
          </button>
        </motion.div>
      )}

      {/* FAMILIA */}
      {tab === "familia" && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-med-text">Familia</h2>
            <p className="text-med-muted text-sm mt-1">
              Personas que puedes gestionar en eventos y asistencia.
            </p>
          </div>

          <div className="border border-med-border rounded-2xl p-4 bg-med-surface2/40">
            <h3 className="font-semibold text-med-text mb-3">
              Solicitar vínculo familiar
            </h3>

            <input
              value={familySearch}
              onChange={(e) => setFamilySearch(e.target.value)}
              placeholder="Buscar fallero..."
              className="w-full rounded-xl bg-white px-4 py-3 border border-med-border outline-none"
            />

            {loadingFamily && (
              <div className="text-sm text-med-muted mt-2">Buscando...</div>
            )}

            {familyResults.length > 0 && (
              <div className="mt-3 border border-med-border rounded-xl overflow-hidden">
                {familyResults.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => {
                      setSelectedFamily(f);
                      setFamilySearch(`${f.nombre} ${f.apellidos}`);
                      setFamilyResults([]);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-med-surface2 border-b border-med-border last:border-b-0"
                  >
                    <div className="font-medium text-med-text">
                      {f.nombre} {f.apellidos}
                    </div>

                    <div className="text-xs text-med-muted">
                      {f.categoria_aplicada}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {selectedFamily && (
              <div className="mt-4">
                <label className="block text-xs font-semibold uppercase tracking-wide text-med-muted mb-1">
                  ¿Qué relación tiene contigo?
                </label>

                <select
                  value={parentesco}
                  onChange={(e) => setParentesco(e.target.value)}
                  className="w-full rounded-xl bg-white px-4 py-3 border border-med-border"
                >
                  {PARENTESCOS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>

                <p className="mt-2 text-sm text-med-muted">
                  Esta persona te verá como:{" "}
                  <span className="mt-2 text-sm text-med-muted">
                    {INVERSE_RELATION_LABELS[parentesco]}
                  </span>
                </p>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.post("/familias/mine", {
                        dependiente_id: selectedFamily.id,
                        parentesco,
                      });

                      const fam = await api.get("/familias/mine");

                      setFamily(fam.data);

                      toast.success("Solicitud enviada");

                      setSelectedFamily(null);

                      setFamilySearch("");
                      await reloadNotifications();
                    } catch (err) {
                      toast.error(
                        err?.response?.data?.detail ||
                          "No se pudo crear la solicitud",
                      );
                    }
                  }}
                  className="mt-4 w-full bg-med-primary hover:bg-med-primary-hover text-white rounded-xl py-3 font-semibold"
                >
                  Enviar solicitud
                </button>
              </div>
            )}
          </div>

          {receivedRequests.length > 0 && (
            <div className="bg-med-surface border border-med-border rounded-2xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-med-text">
                    Solicitudes recibidas
                  </h3>

                  <p className="text-sm text-med-muted mt-1">
                    Solicitudes recibidas pendientes de aprobación.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {receivedRequests.map((req) => (
                  <div
                    key={req.id}
                    className="rounded-xl border border-med-border bg-med-surface2 px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-medium text-med-text">
                          {req.solicitante_nombre || "Solicitud recibida"}
                        </div>

                        <div className="text-sm text-med-muted mt-1">
                          {PARENTESCOS_LABELS[req.solicitante_parentesco] ||
                            "Parentesco"}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            try {
                              await api.post(`/solicitudes/${req.id}/accept`);

                              toast.success("Solicitud aceptada");

                              const requests =
                                await api.get("/solicitudes/mine");

                              setFamilyRequests(requests.data);

                              const fam = await api.get("/familias/mine");

                              setFamily(fam.data);

                              await reloadNotifications();
                            } catch (err) {
                              toast.error("No se pudo aceptar");
                            }
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4 py-2 text-sm font-semibold"
                        >
                          Aceptar
                        </button>

                        <button
                          onClick={async () => {
                            try {
                              await api.post(`/solicitudes/${req.id}/reject`);
                              toast.success("Solicitud rechazada");
                              const requests =
                                await api.get("/solicitudes/mine");
                              setFamilyRequests(requests.data);
                              await reloadNotifications();
                            } catch (err) {
                              toast.error("No se pudo rechazar");
                            }
                          }}
                          className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2 text-sm font-semibold"
                        >
                          Rechazar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sentRequests.length > 0 && (
            <div className="bg-med-surface border border-med-border rounded-2xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-med-text">
                    Solicitudes pendientes
                  </h3>

                  <p className="text-sm text-med-muted mt-1">
                    Solicitudes enviadas pendientes de aprobación.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {sentRequests.map((req) => (
                  <div
                    key={req.id}
                    className="rounded-xl border border-med-border bg-med-surface2 px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-medium text-med-text">
                          {req.objetivo_nombre || "Solicitud enviada"}
                        </div>

                        <div className="text-sm text-med-muted mt-1">
                          {req.approval_type === "admin"
                            ? "Pendiente de aprobación por administración"
                            : "Pendiente de aprobación por el usuario"}
                        </div>
                      </div>

                      <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                        Pendiente
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {family.length === 0 ? (
              <div className="text-med-muted">
                No tienes familiares añadidos.
              </div>
            ) : (
              family.map((f) => (
                <div
                  key={f.id}
                  className="bg-med-surface border border-med-border rounded-2xl p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-11 w-11 rounded-full bg-med-surface2 flex items-center justify-center text-med-text font-semibold">
                          {f.dependiente?.nombre?.[0]}
                        </div>

                        <div>
                          <div className="font-semibold text-lg text-med-text">
                            {f.dependiente?.nombre} {f.dependiente?.apellidos}
                          </div>

                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center rounded-full bg-med-surface2 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-med-muted">
                              {PARENTESCOS_LABELS[f.parentesco]}
                            </span>

                            {f.dependiente?.categoria_aplicada && (
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                                {f.dependiente.categoria_aplicada}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        canManage
                          ? removeFamily(f.id)
                          : requestRemoveFamily(f.id)
                      }
                      className="text-sm font-medium text-red-500 hover:text-red-600"
                    >
                      {canManage ? "Eliminar" : "Solicitar desvinculación"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* SEGURIDAD */}
      {tab === "seguridad" && (
        <motion.form
          initial={{
            opacity: 0,
            y: 10,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          onSubmit={changePassword}
          className="bg-med-surface border border-med-border rounded-2xl p-6 max-w-xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-2xl bg-med-primary/10 text-med-primary grid place-items-center">
              <Lock size={22} weight="duotone" />
            </div>

            <div>
              <h2 className="font-display text-2xl font-bold text-med-text">
                Contraseña
              </h2>

              <p className="text-sm text-med-muted">
                Cambia tu contraseña de acceso
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                Contraseña actual
              </label>

              <input
                type="password"
                value={passwords.actual}
                onChange={(e) =>
                  setPasswords({
                    ...passwords,
                    actual: e.target.value,
                  })
                }
                className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                Nueva contraseña
              </label>

              <input
                type="password"
                value={passwords.nueva}
                onChange={(e) =>
                  setPasswords({
                    ...passwords,
                    nueva: e.target.value,
                  })
                }
                className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                Repetir contraseña
              </label>

              <input
                type="password"
                value={passwords.repetir}
                onChange={(e) =>
                  setPasswords({
                    ...passwords,
                    repetir: e.target.value,
                  })
                }
                className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full mt-6 bg-med-primary hover:bg-med-primary-hover text-white rounded-xl px-5 py-3 font-semibold transition-colors"
          >
            Cambiar contraseña
          </button>
        </motion.form>
      )}
    </div>
  );
}
